import { useEffect, useRef, useState } from 'react'
import { playInCallMessageChime } from '../../../services/audio-manager'
import type { ChatMessage } from '../../../types/chat'

export type InCallChatAlert = {
  /** A chat arrived while the panel was closed — show the badge dot on the button. */
  hasUnread: boolean
  /** The most recent unseen incoming message, for the transient banner (or null). */
  banner: { id: string; senderId: string; text: string } | null
  /** Call when the chat panel/drawer is opened to clear the unread state + banner. */
  clear: () => void
}

/**
 * Surfaces an alert when a chat message arrives DURING a call while the in-call
 * chat panel is closed: a one-shot banner, a subtle chime, and a persistent
 * unread dot on the chat button. Silent for the user's own messages and while
 * the panel is open. Used by the voice/video/group call overlays.
 */
export function useInCallChatAlert(
  messages: ChatMessage[],
  currentUserId: string | undefined,
  panelOpen: boolean,
  /**
   * Optional: true while the initial call-chat history is still loading. When a
   * caller drives this flag, a "loading finished with no history" transition is
   * treated as a genuinely-empty start so the first live message DOES alert.
   * When omitted, the hook falls back to seeding on the first non-empty load.
   */
  historyLoading = false,
): InCallChatAlert {
  const [hasUnread, setHasUnread] = useState(false)
  const [banner, setBanner] = useState<InCallChatAlert['banner']>(null)
  // Seed with the messages present after the first NON-empty / settled load so
  // backfilled history never alerts.
  const seenIdsRef = useRef<Set<string>>(new Set())
  const initializedRef = useRef(false)
  // True once we've actually observed `historyLoading === true`, i.e. the caller
  // is driving the loading flag. Lets us trust a later `false` as "settled".
  const sawLoadingRef = useRef(false)
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (historyLoading) sawLoadingRef.current = true

    // First pass: record existing history as already-seen so the async backfill
    // never alerts. The initial render has an EMPTY `messages` array (history
    // loads async), so we must NOT seed yet — otherwise the later-arriving
    // history would each look "new" and fire a false dot + chime. Wait until:
    //   • the first non-empty load (works with no caller cooperation), or
    //   • loading settles with no history (only when the caller drives
    //     `historyLoading`), so a genuinely-empty call still alerts on its first
    //     live message.
    if (!initializedRef.current) {
      if (messages.length === 0) {
        if (sawLoadingRef.current && !historyLoading) initializedRef.current = true
        return
      }
      for (const m of messages) seenIdsRef.current.add(m.id)
      initializedRef.current = true
      return
    }

    let newestIncoming: ChatMessage | null = null
    for (const m of messages) {
      if (seenIdsRef.current.has(m.id)) continue
      seenIdsRef.current.add(m.id)
      const fromOther = m.senderId ? m.senderId !== currentUserId : m.sender !== 'me'
      if (fromOther) newestIncoming = m
    }

    if (!newestIncoming) return
    if (panelOpen) return

    playInCallMessageChime()
    setHasUnread(true)
    setBanner({
      id: newestIncoming.id,
      senderId: newestIncoming.senderId ?? '',
      text: newestIncoming.text ?? '',
    })
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current)
    bannerTimerRef.current = setTimeout(() => setBanner(null), 5_000)
  }, [messages, currentUserId, panelOpen, historyLoading])

  useEffect(() => {
    if (panelOpen) {
      setHasUnread(false)
      setBanner(null)
    }
  }, [panelOpen])

  useEffect(
    () => () => {
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current)
    },
    [],
  )

  return {
    hasUnread,
    banner,
    clear: () => {
      setHasUnread(false)
      setBanner(null)
    },
  }
}
